import { useCallback, useContext, useMemo, useRef } from 'react'
import { AuthContext } from 'torqcloud-ui'

import { appConfig } from '@/config'
import { useOrganization } from '@/contexts/OrganizationContext'
import { SSEConnectionState, useSSE, SSE } from '@api/sse'
import { QueuedMessageUpdate } from '@slices/userMessage'

const BASE_URL = `${appConfig.flowiseBaseUrl}/api/v1`

/**
 * Prediction SSE event types that mirror the backend event format.
 */
export type PredictionEventType =
  | 'connected'
  | 'session:state'
  | 'message:status'
  | 'start'
  | 'token'
  | 'message'
  | 'files'
  | 'end'
  | 'videoLink'
  | 'videoError'
  | 'sourceDocuments'
  | 'error'

export interface PredictionSSE extends SSE<PredictionEventType> {
  data?: unknown
  sessionId?: string
  requestMessageId?: string
  streamBuffer?: string
  queuedMessages?: QueuedMessageUpdate[]
  message?: string
}

export interface UseSessionSSEOptions {
  chatflowId: string
  sessionId: string | undefined
  onEvent: (event: PredictionSSE) => void
  onDisconnected?: () => void
}

export interface UseSessionSSEResult {
  connectionState: SSEConnectionState
  error: Error | null
  reconnect: () => void
}

export function useSessionSSE(options: UseSessionSSEOptions): UseSessionSSEResult {
  const { chatflowId, sessionId, onEvent, onDisconnected } = options
  const { authenticationManager } = useContext(AuthContext)
  const { currentOrganizationId } = useOrganization()
  const orgIdRef = useRef(currentOrganizationId)
  orgIdRef.current = currentOrganizationId

  const getAuthToken = useCallback(async (): Promise<string> => {
    const session = await authenticationManager.currentSession()
    const token = session?.getIdToken()?.getJwtToken()
    if (!token) {
      throw new Error('No auth token available')
    }
    return token
  }, [authenticationManager])

  const handleEvent = useCallback(
    (event: PredictionSSE) => {
      try {
        onEvent(event)
      } catch (err) {
        console.error('[SessionSSE] Error in event callback:', err)
      }
    },
    [onEvent]
  )

  const sseUrl = useMemo(() => {
    if (!chatflowId || !sessionId) return null
    return `${BASE_URL}/chatflows/${chatflowId}/sessions/${sessionId}/events`
  }, [chatflowId, sessionId])

  const sseConfig = useMemo(() => {
    if (!sseUrl) return null

    return {
      url: sseUrl,
      getAuthToken,
      getOrganizationId: () => orgIdRef.current,
      onEvent: handleEvent,
      onDisconnected,
    }
  }, [sseUrl, getAuthToken, handleEvent, onDisconnected])

  const { state, error, reconnect } = useSSE<PredictionSSE>(sseConfig)

  return {
    connectionState: state,
    error,
    reconnect,
  }
}
